import { AddHospitalRequestPayload } from "@/data/payload/admin/add-hospital-request-payload";
import { HospitalRepository } from "@/domain/repository/hospital-repository";
import { SATError, SATErrorType, SATResult } from "@sadtx/sat-error";
import { translate } from "@/utils/i18n";

export type AddHospitalValidationErrors = {
  hospitalName?: string;
  hospitalCode?: string;
};

export class AddHospitalValidationError extends SATError {
  constructor(public readonly fieldErrors: AddHospitalValidationErrors) {
    super(SATErrorType.ValidationError, translate("hospital.validation.title"));
  }
}

export class AddHospitalUsecase {
  constructor(private readonly hospitalRepo: HospitalRepository) {}

  validate(payload: AddHospitalRequestPayload): AddHospitalValidationErrors {
    const hospitalName = payload.hospitalName.trim();
    const hospitalCode = payload.hospitalCode.trim().toUpperCase();
    const errors: AddHospitalValidationErrors = {};

    if (!hospitalName) {
      errors.hospitalName = translate("hospital.validation.nameRequired");
    } else if (hospitalName.length < 2) {
      errors.hospitalName = translate("hospital.validation.nameMinLength");
    } else if (!/^[가-힣a-zA-Z\s]+$/.test(hospitalName)) {
      errors.hospitalName = translate("hospital.validation.nameCharset");
    }

    if (!hospitalCode) {
      errors.hospitalCode = translate("hospital.validation.codeRequired");
    } else if (!/^[A-Z]{3}$/.test(hospitalCode)) {
      errors.hospitalCode = translate("hospital.validation.codeFormat");
    }

    return errors;
  }

  async execute(payload: AddHospitalRequestPayload): Promise<SATResult<void, SATError>> {
    const normalizedPayload = {
      hospitalName: payload.hospitalName.trim(),
      hospitalCode: payload.hospitalCode.trim().toUpperCase(),
    };
    const validationErrors = this.validate(normalizedPayload);
    if (Object.keys(validationErrors).length > 0) {
      return SATResult.failure(new AddHospitalValidationError(validationErrors));
    }

    return this.hospitalRepo.addHospitalInfo(normalizedPayload);
  }
}
